/**
 * BaselineReport — /assess/report. Final step of the 1-2-1 take flow.
 *
 * WHAT IS SHOWN IS WHAT WAS SAVED (PR #5 review, Sean). When the run persisted, this screen reads
 * the response back from the server and bands the SERVER's score. It used to band the score the
 * browser had computed a moment earlier, under a heading saying the assessment was captured.
 *
 * In the happy path both numbers are the same, which is exactly why the local one was unsafe: the
 * only times they differ are the times it matters — a save that half-succeeded, a stale flow, a row
 * corrected elsewhere. On those the screen was confidently reporting a figure the record did not
 * hold, and this is the last thing the practitioner sees before acting on it. A failed read-back
 * says so rather than falling back to the local number.
 *
 * A run that never persisted (a catalog preview) still bands locally and says plainly that it was
 * not saved — there is no record to disagree with.
 *
 * SAFETY: banding is GUIDANCE, never a diagnosis (docs/platform-upgrade-2026.md
 *   § Safety). The "not a diagnosis" line is always shown.
 *
 * TODO[VER-108/Phase3]: render pre/post comparison once a second data point exists.
 * TODO[RBAC]: gate at the route level with the AssessView permit once defined.
 */
import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Card, CardContent, Divider, Grid, LinearProgress, Typography } from "@mui/material";
import { CheckCircleOutline, InfoOutlined, ReplayOutlined } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useMobile } from "../../../hooks/useMobile";
import Spinner from "../../layout/Spinner";
import { getSurveyResponse, PersistedSurveyResponse } from "../../../client/api/survey";
import { AssessFlowStepper } from "./AssessFlowStepper";
import { useAssessFlow } from "./AssessFlowContext";
import { bandScore } from "./banding";

export const BaselineReport = (): JSX.Element => {
    const { isMobile, isTablet } = useMobile();
    const padding = !isTablet && isMobile ? 2 : 4;
    const navigate = useNavigate();
    const { state, resetFlow } = useAssessFlow();

    const persistedResponseId = state.persistedResponseId;
    const [persisted, setPersisted] = useState<PersistedSurveyResponse | null>(null);
    const [readBackError, setReadBackError] = useState<string | null>(null);
    const [readingBack, setReadingBack] = useState(persistedResponseId != null);

    useEffect(() => {
        if (persistedResponseId == null) {
            setPersisted(null);
            setReadBackError(null);
            setReadingBack(false);
            return;
        }
        let cancelled = false;
        setReadingBack(true);
        setReadBackError(null);
        getSurveyResponse(persistedResponseId)
            .then((row) => {
                if (!cancelled) setPersisted(row);
            })
            .catch((error: unknown) => {
                if (cancelled) return;
                setReadBackError(
                    error instanceof Error ? error.message : "The saved assessment could not be read back.",
                );
            })
            .finally(() => {
                if (!cancelled) setReadingBack(false);
            });
        return () => {
            cancelled = true;
        };
    }, [persistedResponseId]);

    const score = state.score;

    if (!score) {
        return (
            <Box p={padding} width={1}>
                {!isTablet && isMobile && <Box pt={8} />}
                <AssessFlowStepper activeStep={3} />
                <Alert severity="info">
                    No score to report yet — take an assessment first.{" "}
                    <Button variant="text" onClick={() => navigate("/assess/new")}>
                        Start an assessment
                    </Button>
                </Alert>
            </Box>
        );
    }

    // Still fetching the saved row: show nothing rather than the local number, which is the very
    // figure this screen exists to stop presenting as the record.
    if (readingBack) {
        return (
            <Box p={padding} width={1}>
                {!isTablet && isMobile && <Box pt={8} />}
                <AssessFlowStepper activeStep={3} />
                <Spinner />
            </Box>
        );
    }

    /**
     * The number on screen. From the server whenever the run was saved; local only for a preview
     * that never persisted. `rawRange` and `multiplier` come from the definition either way — they
     * describe the instrument, not the run, so they are the same on both sides.
     */
    const reportedScore = persisted
        ? (persisted.finalScore ?? persisted.rawScore ?? score.finalScore)
        : score.finalScore;
    const higherIsBetter = persisted ? persisted.higherIsBetter : score.higherIsBetter;
    const scaleName = persisted?.templateTitle ?? score.scaleName;

    /** Reflects what the server stored, so a session-6 run is not labelled "baseline". */
    const ADMINISTRATION_LABEL: Record<string, string> = {
        Baseline: "start-of-programme measurement captured",
        Interim: "mid-programme measurement captured",
        Endpoint: "end-of-programme measurement captured",
    };
    const captureLabel = persisted
        ? (ADMINISTRATION_LABEL[persisted.administration] ?? "measurement captured")
        : "scored — not saved";

    const band = bandScore(reportedScore, score.rawRange, score.multiplier, higherIsBetter);
    const min = score.rawRange[0] * (score.multiplier ?? 1);
    const max = score.rawRange[1] * (score.multiplier ?? 1);

    return (
        <Box p={padding} width={1}>
            {!isTablet && isMobile && <Box pt={8} />}

            <Typography variant="h2" color="textPrimary" mb={3}>
                Assessment report
            </Typography>

            <AssessFlowStepper activeStep={3} />

            {/*
             * A read-back failure is stated, never papered over with the local score. The
             * practitioner is told the run was saved but this screen cannot show what was stored —
             * which is the truth, and is actionable (open the participant's Impact tab).
             */}
            {readBackError ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    The assessment was saved, but we could not read it back to display here ({readBackError}). Open the
                    participant&apos;s Impact tab to see the stored result — the figures below are this device&apos;s
                    working copy, not the record.
                </Alert>
            ) : null}

            {!persisted && !readBackError ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Preview only — this run was not linked to a participant and session, so nothing was saved.
                </Alert>
            ) : null}

            <Card>
                <CardContent>
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                        <CheckCircleOutline color={persisted ? "success" : "disabled"} />
                        <Typography variant="h5" color="textPrimary">
                            {scaleName} — {captureLabel}
                        </Typography>
                    </Box>

                    <Grid container spacing={3} mb={2}>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="textSecondary">
                                Score
                            </Typography>
                            <Typography variant="h3" color="primary">
                                {reportedScore}
                                <Typography variant="body2" component="span" color="textSecondary" sx={{ ml: 1 }}>
                                    range {min}–{max}
                                </Typography>
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="textSecondary">
                                Wellbeing band
                            </Typography>
                            <Typography variant="h4" color="textPrimary">
                                {band.label}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                                Guidance only — not a diagnosis.
                            </Typography>
                        </Grid>
                    </Grid>

                    {/* Position on the scale */}
                    <Box mb={3}>
                        <Box display="flex" justifyContent="space-between" mb={0.5}>
                            <Typography variant="caption" color="textSecondary">
                                Position on scale
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                                {band.percentOfRange}%
                            </Typography>
                        </Box>
                        <LinearProgress variant="determinate" value={band.percentOfRange} />
                    </Box>

                    <Typography variant="body1" color="textPrimary" mb={2}>
                        {band.interpretation}
                    </Typography>

                    {score.cutoffLabel ? (
                        <Alert severity="info" icon={<InfoOutlined />} sx={{ mb: 2 }}>
                            <Typography variant="body2">
                                Wellbeing guidance: {score.cutoffLabel}. This is general guidance only and is not a
                                diagnosis.
                            </Typography>
                        </Alert>
                    ) : null}

                    {/* Non-diagnostic framing — ALWAYS shown. */}
                    <Alert severity="info" icon={<InfoOutlined />}>
                        <Typography variant="body2">
                            This measurement is a reference point for tracking change over the programme. It is not a
                            clinical diagnosis and should be interpreted alongside professional judgement.
                        </Typography>
                    </Alert>

                    <Divider sx={{ my: 3 }} />

                    <Box display="flex" justifyContent="space-between" gap={2} flexWrap="wrap">
                        <Button
                            variant="text"
                            startIcon={<ReplayOutlined />}
                            onClick={() => {
                                resetFlow();
                                navigate("/assess/new");
                            }}
                        >
                            Start another assessment
                        </Button>
                        <Button variant="contained" color="primary" onClick={() => navigate("/assess")}>
                            Back to Assess hub
                        </Button>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
};

export default BaselineReport;
